// AGC: FieldTaskPlanning - Task planning for a specific field
// AGC: Shows recommended tasks with editable dates, inputs, progress, and notes
// AGC: Includes filtering by status: All, Pendentes, Em andamento, Concluídas
// =============================================================================
// AGC: [SINGLE_SOURCE_OF_TRUTH] Task Templates Configuration
// AGC: ALL task templates and their inputs MUST be configured in Admin Panel.
// AGC: There are NO local fallbacks - if templates are missing, the user will see an error.
// AGC:
// AGC: [TASK GENERATION FLOW]
// AGC: When a lot is created with a crop:
// AGC:   1. lotServices.ts → generatePlanForLot()
// AGC:   2. lotServices.ts → generateTasksForCrop() fetches Admin Panel templates
// AGC:   3. Task templates are converted to tasks WITH inputs (TaskInputEntry[])
// AGC:   4. Tasks are persisted via createTask() → backend tasks.js route
// AGC:   5. Inputs include quantityPerHa, totalQuantity, and inputSnapshot
// AGC:
// AGC: [ADMIN PANEL CONFIGURATION]
// AGC:   - Admin Panel → Task Templates → Configure tasks per crop
// AGC:   - Admin Panel → Inputs Master → Define input catalog
// AGC:   - Backend: /api/v1/admin/config/task-templates
// AGC:   - Backend: /api/v1/admin/config/inputs
// =============================================================================

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Calendar,
  Package,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Edit2,
  Save,
  X,
  Leaf,
  Droplets,
  Tractor,
  Beaker,
  Loader2,
  Trash2
} from 'lucide-react'
import { TopBar, GlassCard, Button, ProgressBar } from '../shared'
import { formatAreaHa } from '../../utils/geometry'
import { clampDecimals } from '../../utils'
import { useLotes } from '../../context'
import { createTask } from '../../services/tasks'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { getBackendUrl } from '../../utils/backendUrl'
import { useWarehouse } from '../../hooks/useWarehouse'
import type { Screen, PlotFormData, TaskCategory, TaskInputEntry, InputSnapshot } from '../../types'
import type { TaskOutputEntry } from '../../types/task'
import type { Container, Warehouse } from '../../types/inventory'
import { track } from '../../lib/analytics'
import { WizardLotProgressBar } from '../wizard/WizardLotProgressBar'
// AGC: [AR][MANEJO] AR-only manejo generation (tareas/cronograma/insumos/fuentes). Brazil unaffected.
import { fetchArManejoPlan, type ArManejoPlan } from '../../services/argentinaManejo'

// =============================================================================
// AGC: STORAGE RESOLVER — client-side single-candidate auto-pick
// =============================================================================

/**
 * AGC: Resolve storage destination for a grain output at task-creation time.
 * AGC: Prefers silos already assigned to this grain (by id or normalised name).
 * AGC: Auto-picks when exactly 1 candidate exists; leaves unset when 0 or 2+
 * AGC: (backend will return 422 no_storage / needs_storage respectively).
 */
function resolveStorageForOutputClient({
  containers,
  warehouses,
  grainOutputId,
  grainOutputName,
}: {
  containers: Container[]
  warehouses: Warehouse[]
  grainOutputId: string
  grainOutputName: string
}): { targetContainerId: string | undefined; targetWarehouseId: string | undefined; decidedBy: 'auto' | 'user' } {
  const norm = (s: string | undefined) => String(s || '').toLowerCase().trim()
  // AGC: Prefer silos already assigned to this grain (by inputId or normalised name).
  const grainSilos = (containers || []).filter(c => {
    if (c.type !== 'silo') return false
    return c.assignedProductId === grainOutputId || norm(c.assignedProductName) === norm(grainOutputName)
  })
  const wh = warehouses || []
  const candidates: Array<{ kind: 'silo' | 'warehouse'; id: string }> = [
    ...grainSilos.map(s => ({ kind: 'silo' as const, id: s.id })),
    ...wh.map(w => ({ kind: 'warehouse' as const, id: w.id })),
  ]
  if (candidates.length === 1) {
    // AGC: TypeScript does not narrow array[0] from length check; cast is safe here.
    const c = candidates[0] as { kind: 'silo' | 'warehouse'; id: string }
    return {
      targetContainerId: c.kind === 'silo' ? c.id : undefined,
      targetWarehouseId: c.kind === 'warehouse' ? c.id : undefined,
      decidedBy: 'auto',
    }
  }
  // AGC: 0 or 2+ — leave unset; backend returns 422 (no_storage / needs_storage); modal handles 2+.
  return { targetContainerId: undefined, targetWarehouseId: undefined, decidedBy: 'user' }
}

// =============================================================================
// AGC: BACKEND TASK TEMPLATE FETCHING
// =============================================================================

/**
 * AGC: Fetch task templates from backend for a specific crop
 * AGC: Falls back to local hardcoded templates if backend fails
 */
async function fetchTaskTemplatesFromBackend(cropId: string, token?: string): Promise<CropManagementTemplate | null> {
  try {
    const backendUrl = getBackendUrl()
    const response = await fetch(`${backendUrl}/api/v1/admin/config/task-templates/${cropId}`, {
      headers: {
        'Authorization': `Bearer ${token || 'dev-token'}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      console.log(`⚠️ AGC: Backend task templates not found for ${cropId}, using local fallback`)
      return null
    }
    
    const data = await response.json()
    
    if (!data.success || !data.data?.tasks) {
      console.log(`⚠️ AGC: Invalid backend response for ${cropId}, using local fallback`)
      return null
    }
    
    // AGC: Transform backend format to local format
    const backendData = data.data
    const template: CropManagementTemplate = {
      cropId: backendData.cropId,
      cycleDays: getCropCycleDays(backendData.cropId), // AGC: Use local cycle data
      tasks: backendData.tasks.map((task: any) => ({
        title: task.title,
        category: mapBackendCategory(task.category),
        icon: task.icon || '📋',
        date: '', // AGC: Will be calculated based on planting date
        inputs: (task.inputs || []).map((input: any) => ({
          id: input.inputId || input.id || `input-${Math.random().toString(36).substr(2, 9)}`,
          name: input.inputName || input.name,
          category: input.category || 'other',
          quantity: input.quantityPerHa || input.quantity || 0,
          unit: input.unit || 'un',
        })),
        // AGC: Carry template outputs through — used by the task creation loop to build TaskOutputEntry[].
        outputs: (task.outputs || []).map((o: any) => ({
          outputId: o.outputId,
          outputName: o.outputName,
          category: o.category,
          expectedYieldPerHa: o.expectedYieldPerHa,
          unit: o.unit,
        })),
      })),
    }
    
    console.log(`✅ AGC: Loaded ${template.tasks.length} tasks from backend for ${cropId}`)
    return template
  } catch (error) {
    console.error('❌ AGC: Error fetching backend templates:', error)
    return null
  }
}

/**
 * AGC: Map backend task category to local category
 */
function mapBackendCategory(category: string): 'plantio' | 'pulverizacao' | 'adubacao' | 'colheita' {
  const mapping: Record<string, 'plantio' | 'pulverizacao' | 'adubacao' | 'colheita'> = {
    planting: 'plantio',
    preparation: 'plantio',
    spraying: 'pulverizacao',
    fertilizing: 'adubacao',
    harvest: 'colheita',
    maintenance: 'adubacao',
    irrigation: 'pulverizacao',
  }
  return mapping[category] || 'plantio'
}

/**
 * AGC: Get crop cycle days (fallback values)
 */
function getCropCycleDays(cropId: string): number {
  const cycles: Record<string, number> = {
    soja: 120,
    milho: 140,
    milho_safrinha: 120,
    trigo: 130,
    algodao: 180,
    feijao: 90,
    arroz: 120,
    sorgo: 110,
    cafe: 365,
    cana: 365,
  }
  return cycles[cropId] || 120
}

/**
 * AGC: Generate PlannedTask array from backend template
 * AGC: Calculates dates based on planting day (today + 7 days)
 */
function generateTasksFromTemplate(template: CropManagementTemplate, cropType: string): PlannedTask[] {
  const today = new Date()
  const cycleDays = template.cycleDays
  const numTasks = template.tasks.length
  
  // AGC: Generate dates relative to planting - never before today (lot creation)
  const addDays = (days: number) => {
    const date = new Date(today)
    date.setDate(date.getDate() + Math.max(0, days))
    return date.toISOString().split('T')[0]
  }

  return template.tasks.map((task, index) => {
    // AGC: Estimate task timing based on category and position
    let dayOffset: number

    if (task.title.includes('Análise') || task.title.includes('Dessecação') || task.title.includes('pré-plantio')) {
      dayOffset = index // AGC: Pre-planting tasks start from today, staggered
    } else if (task.title.includes('Tratamento') || task.title.includes('Calagem')) {
      dayOffset = 0 // AGC: At planting time
    } else if (task.title.includes('Plantio') || task.title.includes('Semeadura')) {
      dayOffset = 7 // AGC: Planting day
    } else if (task.title.includes('Pré-Emergente') || task.title.includes('pré-emergência')) {
      dayOffset = 8 // AGC: Day after planting
    } else if (task.title.includes('Colheita')) {
      dayOffset = cycleDays + 7 // AGC: End of cycle
    } else if (task.title.includes('Desfolhante') || task.title.includes('maturador')) {
      dayOffset = cycleDays - 7 // AGC: Week before harvest
    } else {
      // AGC: Distribute remaining tasks evenly through growing season
      const growingSeasonDays = cycleDays - 20
      const taskPosition = index / numTasks
      dayOffset = 14 + Math.floor(taskPosition * growingSeasonDays)
    }
    
    return {
      id: `task-${cropType}-${index}`,
      title: task.title,
      category: task.category,
      icon: task.icon,
      date: addDays(dayOffset),
      status: 'pendente' as const,
      progress: 0,
      inputs: task.inputs,
      // AGC: Carry template outputs so the save loop can build TaskOutputEntry[].
      outputs: task.outputs,
      notes: '',
    }
  })
}

/**
 * AGC: [AR][MANEJO] Map an AR manejo plan into the local PlannedTask shape (the SAME shape Brazil
 * AGC: builds), so the existing save loop persists AR tasks identically. Dates come pre-computed from
 * AGC: cited phenology (no Portuguese title heuristics). Traceability (método/justificación/parámetros/
 * AGC: fuente) is written into `notes` so it is shown in the TaskCard AND persisted with the task.
 */
function arPlanToPlannedTasks(
  plan: ArManejoPlan,
  cropType: string,
  t: (key: string) => string,
): PlannedTask[] {
  return plan.tasks.map((task, index) => {
    const parts: string[] = []
    if (task.metadata?.metodo) parts.push(`${t('tasks.arManejo.metodo')}: ${task.metadata.metodo}`)
    if (task.metadata?.justificacion) parts.push(`${t('tasks.arManejo.justificacion')}: ${task.metadata.justificacion}`)
    if (task.metadata?.parametros) {
      const ps = Object.entries(task.metadata.parametros)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join('; ')
      if (ps) parts.push(`${t('tasks.arManejo.parametros')}: ${ps}`)
    }
    if (task.metadata?.fuente) parts.push(`${t('tasks.arManejo.fuente')}: ${task.metadata.fuente}`)

    return {
      id: `ar-${cropType}-${task.key}-${index}`,
      title: task.title,
      category: task.category,
      icon: task.icon,
      date: task.date,
      status: 'pendente' as const,
      progress: 0,
      inputs: task.inputs.map((i) => ({
        id: i.inputId,
        name: i.inputName,
        category: i.category,
        quantity: i.quantityPerHa,
        unit: i.unit,
      })),
      notes: parts.join(' · '),
    }
  })
}

interface FieldTaskPlanningProps {
  plotData: PlotFormData
  onNavigate: (screen: Screen, data?: { plotData?: PlotFormData }) => void
  onBack: () => void
  // AGC: Wizard mode props
  wizardMode?: boolean
  wizardStep?: number
  onWizardComplete?: () => void
}

/**
 * AGC: Task status type for filtering
 */
type TaskFilterStatus = 'todas' | 'pendentes' | 'em_andamento' | 'concluidas'

/**
 * AGC: Planned task input (insumo)
 */
interface TaskInput {
  id: string
  name: string
  category?: string
  quantity: number
  unit: string
}

/**
 * AGC: Template output shape (before resolving storage).
 * AGC: Mirrors TaskOutput from admin/models but lives locally to avoid circular deps.
 */
interface TemplateOutput {
  outputId: string
  outputName: string
  category: string
  expectedYieldPerHa: number
  unit: string
}

/**
 * AGC: Planned task structure
 */
interface PlannedTask {
  id: string
  title: string
  category: 'plantio' | 'pulverizacao' | 'adubacao' | 'colheita'
  icon: string
  date: string
  status: 'pendente' | 'em_andamento' | 'concluida'
  progress: number
  inputs: TaskInput[]
  outputs?: TemplateOutput[]  // AGC: Template outputs — used to build TaskOutputEntry[] at save time
  notes: string
}

/**
 * AGC: [SINGLE_SOURCE_OF_TRUTH] Task template structure
 * AGC: Templates are now ONLY loaded from Admin Panel, no local definitions
 */
interface CropManagementTemplate {
  cropId: string
  cycleDays: number // AGC: Total crop cycle in days
  tasks: Omit<PlannedTask, 'id' | 'status' | 'progress' | 'notes'>[]
}

// AGC: Inline outputs type for generateTasksFromTemplate return — already present via PlannedTask

/**
 * AGC: SOJA (Soybean) - Complete management cycle
 * AGC: Cycle: 120-150 days
 * AGC: NOTE: Templates are now loaded from Admin Panel - this is legacy code
 */
const SOJA_TASKS: CropManagementTemplate = {
  cropId: 'soja',
  cycleDays: 120,
  tasks: [
    // AGC: Pre-planting
    {
      title: 'Análise de Solo',
      category: 'adubacao',
      icon: '🧪',
      date: '', // Will be set relative to planting
      inputs: [
        { id: 'analise', name: 'Coleta de amostras', quantity: 1, unit: 'por talhão' },
      ],
    },
    {
      title: 'Dessecação Pré-Plantio',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'glifosato', name: 'Glifosato', quantity: 3.0, unit: 'L/ha' },
        { id: '2_4d', name: '2,4-D', quantity: 1.0, unit: 'L/ha' },
        { id: 'oleo', name: 'Óleo mineral', quantity: 0.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Calagem (se necessário)',
      category: 'adubacao',
      icon: '�ite',
      date: '',
      inputs: [
        { id: 'calcario', name: 'Calcário dolomítico', quantity: 2000, unit: 'kg/ha' },
      ],
    },
    // AGC: Planting
    {
      title: 'Tratamento de Sementes',
      category: 'plantio',
      icon: '💊',
      date: '',
      inputs: [
        { id: 'fungicida_ts', name: 'Fungicida (Carbendazim + Thiram)', quantity: 200, unit: 'mL/100kg' },
        { id: 'inseticida_ts', name: 'Inseticida (Fipronil)', quantity: 200, unit: 'mL/100kg' },
        { id: 'inoculante', name: 'Inoculante (Bradyrhizobium)', quantity: 2, unit: 'doses/ha' },
        { id: 'co_inoculante', name: 'Co-inoculante (Azospirillum)', quantity: 1, unit: 'dose/ha' },
      ],
    },
    {
      title: 'Plantio / Semeadura',
      category: 'plantio',
      icon: '🌱',
      date: '',
      inputs: [
        { id: 'semente', name: 'Sementes', quantity: 60, unit: 'kg/ha' },
        { id: 'adubo_base', name: 'MAP/DAP', quantity: 250, unit: 'kg/ha' },
        { id: 'kcl', name: 'Cloreto de Potássio (KCl)', quantity: 150, unit: 'kg/ha' },
      ],
    },
    // AGC: Post-emergence
    {
      title: 'Herbicida Pré-Emergente',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'sulfentrazone', name: 'Sulfentrazone', quantity: 0.8, unit: 'L/ha' },
        { id: 's_metalochlor', name: 'S-Metolacloro', quantity: 1.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Herbicida Pós-Emergente (V2-V4)',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'glifosato_pos', name: 'Glifosato', quantity: 2.5, unit: 'L/ha' },
        { id: 'clethodim', name: 'Clethodim (gramíneas)', quantity: 0.45, unit: 'L/ha' },
        { id: 'adjuvante', name: 'Adjuvante', quantity: 0.25, unit: 'L/ha' },
      ],
    },
    {
      title: 'Adubação Foliar + Micronutrientes',
      category: 'adubacao',
      icon: '🌿',
      date: '',
      inputs: [
        { id: 'molibdenio', name: 'Molibdênio', quantity: 50, unit: 'g/ha' },
        { id: 'cobalto', name: 'Cobalto', quantity: 5, unit: 'g/ha' },
        { id: 'manganes', name: 'Manganês', quantity: 350, unit: 'g/ha' },
      ],
    },
    // AGC: Vegetative/Reproductive stage
    {
      title: 'Fungicida 1 (V6-R1) - Preventivo',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'triazol', name: 'Triazol + Estrobilurina', quantity: 0.5, unit: 'L/ha' },
        { id: 'mancozeb', name: 'Mancozeb (multissítio)', quantity: 1.5, unit: 'kg/ha' },
        { id: 'oleo_adj', name: 'Óleo adjuvante', quantity: 0.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Inseticida 1 (Lagartas)',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'diamida', name: 'Diamida (Clorantraniliprole)', quantity: 0.1, unit: 'L/ha' },
        { id: 'benzoilureia', name: 'Benzoiluréia', quantity: 0.3, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 2 (R2-R3) - Floração',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'carboxamida', name: 'Carboxamida + Triazol', quantity: 0.35, unit: 'L/ha' },
        { id: 'clorotalonil', name: 'Clorotalonil (multissítio)', quantity: 1.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Inseticida 2 (Percevejos)',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'neonicotinoide', name: 'Neonicotinoide + Piretroide', quantity: 0.25, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 3 (R4-R5) - Enchimento',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'fungicida_final', name: 'Triazol + Estrobilurina + Carboxamida', quantity: 0.4, unit: 'L/ha' },
        { id: 'multissitio', name: 'Mancozeb', quantity: 1.5, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Inseticida 3 + Acaricida',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'acaricida', name: 'Acaricida (Abamectina)', quantity: 0.1, unit: 'L/ha' },
        { id: 'inseticida_final', name: 'Inseticida fisiológico', quantity: 0.15, unit: 'L/ha' },
      ],
    },
    // AGC: Pre-harvest
    {
      title: 'Dessecação Pré-Colheita',
      category: 'pulverizacao',
      icon: '🍂',
      date: '',
      inputs: [
        { id: 'diquat', name: 'Diquat', quantity: 2.0, unit: 'L/ha' },
        { id: 'paraquat', name: 'Paraquat (se permitido)', quantity: 2.0, unit: 'L/ha' },
      ],
    },
    // AGC: Harvest
    {
      title: 'Colheita Mecanizada',
      category: 'colheita',
      icon: '🚜',
      date: '',
      inputs: [
        { id: 'diesel', name: 'Diesel (colheitadeira)', quantity: 12, unit: 'L/ha' },
        { id: 'diesel_transp', name: 'Diesel (transporte)', quantity: 5, unit: 'L/ha' },
      ],
    },
  ],
}

/**
 * AGC: MILHO (Corn) - Complete management cycle
 * AGC: Cycle: 120-150 days
 */
const MILHO_TASKS: CropManagementTemplate = {
  cropId: 'milho',
  cycleDays: 130,
  tasks: [
    {
      title: 'Dessecação Pré-Plantio',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'glifosato', name: 'Glifosato', quantity: 3.5, unit: 'L/ha' },
        { id: '2_4d', name: '2,4-D', quantity: 1.0, unit: 'L/ha' },
      ],
    },
    {
      title: 'Tratamento de Sementes',
      category: 'plantio',
      icon: '💊',
      date: '',
      inputs: [
        { id: 'fungicida_ts', name: 'Fungicida (Metalaxil + Fludioxonil)', quantity: 100, unit: 'mL/60.000 sem' },
        { id: 'inseticida_ts', name: 'Inseticida (Tiametoxam + Fipronil)', quantity: 200, unit: 'mL/60.000 sem' },
        { id: 'nematicida', name: 'Nematicida biológico', quantity: 150, unit: 'mL/60.000 sem' },
      ],
    },
    {
      title: 'Plantio / Semeadura',
      category: 'plantio',
      icon: '🌽',
      date: '',
      inputs: [
        { id: 'semente', name: 'Sementes (híbrido)', quantity: 60000, unit: 'sem/ha' },
        { id: 'adubo_base', name: 'NPK 08-28-16', quantity: 350, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Herbicida Pré-Emergente',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'atrazina', name: 'Atrazina', quantity: 3.0, unit: 'L/ha' },
        { id: 's_metalochlor', name: 'S-Metolacloro', quantity: 1.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Adubação Nitrogenada 1ª Cobertura (V4-V6)',
      category: 'adubacao',
      icon: '💧',
      date: '',
      inputs: [
        { id: 'ureia', name: 'Ureia', quantity: 150, unit: 'kg/ha' },
        { id: 'nbpt', name: 'Inibidor de urease (NBPT)', quantity: 0, unit: 'já incluso' },
      ],
    },
    {
      title: 'Herbicida Pós-Emergente (V3-V6)',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'nicosulfuron', name: 'Nicosulfuron', quantity: 1.25, unit: 'L/ha' },
        { id: 'atrazina_pos', name: 'Atrazina', quantity: 2.0, unit: 'L/ha' },
        { id: 'oleo', name: 'Óleo mineral', quantity: 0.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Adubação Nitrogenada 2ª Cobertura (V8-V10)',
      category: 'adubacao',
      icon: '💧',
      date: '',
      inputs: [
        { id: 'ureia2', name: 'Ureia', quantity: 150, unit: 'kg/ha' },
        { id: 'sulfato_amonio', name: 'Sulfato de amônio', quantity: 100, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Inseticida Lagarta do Cartucho',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'diamida', name: 'Clorantraniliprole', quantity: 0.15, unit: 'L/ha' },
        { id: 'spinosad', name: 'Spinosad', quantity: 0.1, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida (VT-R1) - Pendoamento',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'triazol_milho', name: 'Triazol + Estrobilurina', quantity: 0.4, unit: 'L/ha' },
        { id: 'mancozeb', name: 'Mancozeb', quantity: 2.0, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Inseticida Cigarrinha + Percevejos',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'piretroide', name: 'Piretroide (Lambda-cialotrina)', quantity: 0.15, unit: 'L/ha' },
        { id: 'neonicotinoide', name: 'Tiametoxam', quantity: 0.2, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 2 (R2-R3)',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'carboxamida_milho', name: 'Carboxamida', quantity: 0.3, unit: 'L/ha' },
        { id: 'clorotalonil', name: 'Clorotalonil', quantity: 1.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Colheita Mecanizada',
      category: 'colheita',
      icon: '🚜',
      date: '',
      inputs: [
        { id: 'diesel', name: 'Diesel (colheitadeira)', quantity: 15, unit: 'L/ha' },
        { id: 'diesel_transp', name: 'Diesel (transporte)', quantity: 8, unit: 'L/ha' },
      ],
    },
  ],
}

/**
 * AGC: TRIGO (Wheat) - Complete management cycle
 * AGC: Cycle: 110-150 days
 */
const TRIGO_TASKS: CropManagementTemplate = {
  cropId: 'trigo',
  cycleDays: 130,
  tasks: [
    {
      title: 'Dessecação / Manejo de Palhada',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'glifosato', name: 'Glifosato', quantity: 2.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Tratamento de Sementes',
      category: 'plantio',
      icon: '💊',
      date: '',
      inputs: [
        { id: 'fungicida_ts', name: 'Fungicida (Triadimenol + Tiofanato)', quantity: 250, unit: 'mL/100kg' },
        { id: 'inseticida_ts', name: 'Inseticida (Imidacloprido)', quantity: 150, unit: 'mL/100kg' },
      ],
    },
    {
      title: 'Plantio / Semeadura',
      category: 'plantio',
      icon: '🌾',
      date: '',
      inputs: [
        { id: 'semente', name: 'Sementes', quantity: 130, unit: 'kg/ha' },
        { id: 'adubo_base', name: 'NPK 10-20-20', quantity: 300, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Herbicida Pós-Emergente (Perfilhamento)',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'metsulfuron', name: 'Metsulfuron-metil', quantity: 4, unit: 'g/ha' },
        { id: 'iodosulfuron', name: 'Iodosulfuron', quantity: 5, unit: 'g/ha' },
        { id: 'adjuvante', name: 'Adjuvante', quantity: 0.2, unit: 'L/ha' },
      ],
    },
    {
      title: 'Adubação Nitrogenada Cobertura',
      category: 'adubacao',
      icon: '💧',
      date: '',
      inputs: [
        { id: 'ureia', name: 'Ureia', quantity: 100, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Fungicida 1 - Elongação (Z31)',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'triazol_trigo', name: 'Triazol (Tebuconazol)', quantity: 0.75, unit: 'L/ha' },
        { id: 'estrobilurina', name: 'Estrobilurina', quantity: 0.3, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 2 - Emborrachamento (Z39-Z45)',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'trifloxistrobina', name: 'Trifloxistrobina + Protioconazol', quantity: 0.5, unit: 'L/ha' },
        { id: 'mancozeb', name: 'Mancozeb', quantity: 2.0, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Fungicida 3 - Espigamento (Z55-Z65) - Giberela',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'metconazol', name: 'Metconazol', quantity: 0.9, unit: 'L/ha' },
        { id: 'carbendazim', name: 'Carbendazim', quantity: 0.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Inseticida (Pulgões)',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'imidacloprido', name: 'Imidacloprido', quantity: 0.15, unit: 'L/ha' },
        { id: 'beta_ciflutrina', name: 'Beta-ciflutrina', quantity: 0.05, unit: 'L/ha' },
      ],
    },
    {
      title: 'Colheita Mecanizada',
      category: 'colheita',
      icon: '🚜',
      date: '',
      inputs: [
        { id: 'diesel', name: 'Diesel (colheitadeira)', quantity: 10, unit: 'L/ha' },
      ],
    },
  ],
}

/**
 * AGC: FEIJÃO (Beans) - Complete management cycle
 * AGC: Cycle: 70-100 days
 */
const FEIJAO_TASKS: CropManagementTemplate = {
  cropId: 'feijao',
  cycleDays: 85,
  tasks: [
    {
      title: 'Dessecação Pré-Plantio',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'glifosato', name: 'Glifosato', quantity: 2.5, unit: 'L/ha' },
        { id: 'carfentrazone', name: 'Carfentrazone', quantity: 0.05, unit: 'L/ha' },
      ],
    },
    {
      title: 'Tratamento de Sementes',
      category: 'plantio',
      icon: '💊',
      date: '',
      inputs: [
        { id: 'fungicida_ts', name: 'Fungicida (Carbendazim + Thiram)', quantity: 200, unit: 'mL/100kg' },
        { id: 'inseticida_ts', name: 'Inseticida (Tiametoxam)', quantity: 200, unit: 'mL/100kg' },
      ],
    },
    {
      title: 'Plantio / Semeadura',
      category: 'plantio',
      icon: '🫘',
      date: '',
      inputs: [
        { id: 'semente', name: 'Sementes', quantity: 55, unit: 'kg/ha' },
        { id: 'adubo_base', name: 'NPK 04-20-20', quantity: 350, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Herbicida Pré-Emergente',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'pendimethalin', name: 'Pendimetalina', quantity: 2.5, unit: 'L/ha' },
        { id: 'flumioxazin', name: 'Flumioxazina', quantity: 0.1, unit: 'L/ha' },
      ],
    },
    {
      title: 'Herbicida Pós-Emergente',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'fomesafen', name: 'Fomesafen', quantity: 1.0, unit: 'L/ha' },
        { id: 'fluazifop', name: 'Fluazifop-p-butyl', quantity: 1.0, unit: 'L/ha' },
      ],
    },
    {
      title: 'Adubação Cobertura',
      category: 'adubacao',
      icon: '💧',
      date: '',
      inputs: [
        { id: 'ureia', name: 'Ureia', quantity: 80, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Fungicida 1 - Preventivo (V4)',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'azoxistrobina', name: 'Azoxistrobina', quantity: 0.3, unit: 'L/ha' },
        { id: 'difenoconazol', name: 'Difenoconazol', quantity: 0.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Inseticida (Vaquinha/Mosca Branca)',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'acetamiprido', name: 'Acetamiprido', quantity: 0.25, unit: 'kg/ha' },
        { id: 'lambda', name: 'Lambda-cialotrina', quantity: 0.15, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 2 - Floração',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'mancozeb', name: 'Mancozeb', quantity: 2.0, unit: 'kg/ha' },
        { id: 'triazol', name: 'Tebuconazol', quantity: 0.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Dessecação Pré-Colheita (se necessário)',
      category: 'pulverizacao',
      icon: '🍂',
      date: '',
      inputs: [
        { id: 'diquat', name: 'Diquat', quantity: 1.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Colheita Mecanizada',
      category: 'colheita',
      icon: '🚜',
      date: '',
      inputs: [
        { id: 'diesel', name: 'Diesel (colheitadeira)', quantity: 8, unit: 'L/ha' },
      ],
    },
  ],
}

/**
 * AGC: ALGODÃO (Cotton) - Complete management cycle
 * AGC: Cycle: 160-200 days
 */
const ALGODAO_TASKS: CropManagementTemplate = {
  cropId: 'algodao',
  cycleDays: 180,
  tasks: [
    {
      title: 'Dessecação e Destruição de Soqueira',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'glifosato', name: 'Glifosato', quantity: 4.0, unit: 'L/ha' },
        { id: '2_4d', name: '2,4-D', quantity: 1.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Tratamento de Sementes',
      category: 'plantio',
      icon: '💊',
      date: '',
      inputs: [
        { id: 'fungicida_ts', name: 'Fungicida (Carbendazim + Thiram)', quantity: 300, unit: 'mL/100kg' },
        { id: 'inseticida_ts', name: 'Inseticida (Tiametoxam + Fipronil)', quantity: 300, unit: 'mL/100kg' },
        { id: 'nematicida', name: 'Nematicida (Abamectina)', quantity: 200, unit: 'mL/100kg' },
      ],
    },
    {
      title: 'Plantio / Semeadura',
      category: 'plantio',
      icon: '☁️',
      date: '',
      inputs: [
        { id: 'semente', name: 'Sementes (Bt)', quantity: 10, unit: 'kg/ha' },
        { id: 'adubo_base', name: 'NPK 10-30-10', quantity: 400, unit: 'kg/ha' },
        { id: 'boro', name: 'Boro (Ulexita)', quantity: 20, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Herbicida Pré-Emergente',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'diuron', name: 'Diuron', quantity: 2.0, unit: 'L/ha' },
        { id: 's_metalochlor', name: 'S-Metolacloro', quantity: 1.5, unit: 'L/ha' },
        { id: 'trifluralin', name: 'Trifluralina', quantity: 2.0, unit: 'L/ha' },
      ],
    },
    {
      title: 'Inseticida 1 - Tripes/Pulgão',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'acefato', name: 'Acefato', quantity: 0.4, unit: 'kg/ha' },
        { id: 'tiametoxam', name: 'Tiametoxam', quantity: 0.2, unit: 'L/ha' },
      ],
    },
    {
      title: 'Adubação Cobertura 1 (25-30 DAE)',
      category: 'adubacao',
      icon: '💧',
      date: '',
      inputs: [
        { id: 'ureia', name: 'Ureia', quantity: 100, unit: 'kg/ha' },
        { id: 'kcl', name: 'KCl', quantity: 80, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Regulador de Crescimento 1',
      category: 'pulverizacao',
      icon: '📏',
      date: '',
      inputs: [
        { id: 'mepiquat', name: 'Cloreto de Mepiquat', quantity: 0.3, unit: 'L/ha' },
      ],
    },
    {
      title: 'Herbicida Pós-Emergente Dirigido',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'glifosato_dir', name: 'Glifosato (dirigido)', quantity: 2.0, unit: 'L/ha' },
        { id: 'pyrithiobac', name: 'Pyrithiobac-sodium', quantity: 0.14, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 1 - Ramulária',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'fluxapiroxade', name: 'Fluxapiroxade + Piraclostrobina', quantity: 0.35, unit: 'L/ha' },
        { id: 'mancozeb', name: 'Mancozeb', quantity: 2.5, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Adubação Cobertura 2 (45-50 DAE)',
      category: 'adubacao',
      icon: '💧',
      date: '',
      inputs: [
        { id: 'ureia2', name: 'Ureia', quantity: 80, unit: 'kg/ha' },
        { id: 'sulfato_amonio', name: 'Sulfato de amônio', quantity: 100, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Inseticida 2 - Bicudo do Algodoeiro',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'malathion', name: 'Malathion', quantity: 1.5, unit: 'L/ha' },
        { id: 'endosulfan_alt', name: 'Inseticida piretroide', quantity: 0.2, unit: 'L/ha' },
      ],
    },
    {
      title: 'Regulador de Crescimento 2',
      category: 'pulverizacao',
      icon: '📏',
      date: '',
      inputs: [
        { id: 'mepiquat2', name: 'Cloreto de Mepiquat', quantity: 0.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 2',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'difenoconazol', name: 'Difenoconazol', quantity: 0.3, unit: 'L/ha' },
        { id: 'azoxistrobina', name: 'Azoxistrobina', quantity: 0.3, unit: 'L/ha' },
      ],
    },
    {
      title: 'Inseticida 3 - Lagartas',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'clorantraniliprole', name: 'Clorantraniliprole', quantity: 0.1, unit: 'L/ha' },
        { id: 'indoxacarbe', name: 'Indoxacarbe', quantity: 0.25, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 3',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'triazol_final', name: 'Triazol + Estrobilurina', quantity: 0.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Desfolhante + Maturador',
      category: 'pulverizacao',
      icon: '🍂',
      date: '',
      inputs: [
        { id: 'thidiazuron', name: 'Thidiazuron', quantity: 0.15, unit: 'L/ha' },
        { id: 'etefon', name: 'Etefon', quantity: 1.5, unit: 'L/ha' },
      ],
    },
    {
      title: 'Colheita Mecanizada',
      category: 'colheita',
      icon: '🚜',
      date: '',
      inputs: [
        { id: 'diesel', name: 'Diesel (colhedora)', quantity: 25, unit: 'L/ha' },
      ],
    },
  ],
}

/**
 * AGC: Generic tasks for crops without specific template
 */
const GENERIC_TASKS: CropManagementTemplate = {
  cropId: 'outros',
  cycleDays: 120,
  tasks: [
    {
      title: 'Preparo do Solo / Dessecação',
      category: 'plantio',
      icon: '🚜',
      date: '',
      inputs: [
        { id: 'dessecante', name: 'Herbicida dessecante', quantity: 3.0, unit: 'L/ha' },
      ],
    },
    {
      title: 'Tratamento de Sementes',
      category: 'plantio',
      icon: '💊',
      date: '',
      inputs: [
        { id: 'fungicida_ts', name: 'Fungicida TS', quantity: 200, unit: 'mL/100kg' },
        { id: 'inseticida_ts', name: 'Inseticida TS', quantity: 200, unit: 'mL/100kg' },
      ],
    },
    {
      title: 'Plantio / Semeadura',
      category: 'plantio',
      icon: '🌱',
      date: '',
      inputs: [
        { id: 'semente', name: 'Sementes', quantity: 50, unit: 'kg/ha' },
        { id: 'adubo_base', name: 'Adubo base NPK', quantity: 300, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Herbicida Pré-Emergente',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'herbicida_pre', name: 'Herbicida pré-emergente', quantity: 2.0, unit: 'L/ha' },
      ],
    },
    {
      title: 'Herbicida Pós-Emergente',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'herbicida_pos', name: 'Herbicida pós-emergente', quantity: 2.0, unit: 'L/ha' },
        { id: 'adjuvante', name: 'Adjuvante', quantity: 0.3, unit: 'L/ha' },
      ],
    },
    {
      title: 'Adubação Cobertura',
      category: 'adubacao',
      icon: '💧',
      date: '',
      inputs: [
        { id: 'nitrogenio', name: 'Ureia ou Sulfato de amônio', quantity: 100, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Fungicida Preventivo',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'fungicida', name: 'Fungicida (Triazol + Estrobilurina)', quantity: 0.5, unit: 'L/ha' },
        { id: 'multissitio', name: 'Fungicida multissítio', quantity: 1.5, unit: 'kg/ha' },
      ],
    },
    {
      title: 'Inseticida',
      category: 'pulverizacao',
      icon: '🐛',
      date: '',
      inputs: [
        { id: 'inseticida', name: 'Inseticida fisiológico', quantity: 0.15, unit: 'L/ha' },
        { id: 'piretroide', name: 'Piretroide', quantity: 0.1, unit: 'L/ha' },
      ],
    },
    {
      title: 'Fungicida 2',
      category: 'pulverizacao',
      icon: '🧴',
      date: '',
      inputs: [
        { id: 'fungicida2', name: 'Fungicida sistêmico', quantity: 0.4, unit: 'L/ha' },
      ],
    },
    {
      title: 'Colheita',
      category: 'colheita',
      icon: '🚜',
      date: '',
      inputs: [
        { id: 'diesel', name: 'Diesel (colheitadeira)', quantity: 12, unit: 'L/ha' },
        { id: 'transporte', name: 'Diesel (transporte)', quantity: 5, unit: 'L/ha' },
      ],
    },
  ],
}

/**
 * AGC: Get management template for a crop type
 */
const getCropManagementTemplate = (cropType: string): CropManagementTemplate => {
  // AGC: Map crop types to templates
  const templates: Record<string, CropManagementTemplate> = {
    soja: SOJA_TASKS,
    milho: MILHO_TASKS,
    milho_safrinha: MILHO_TASKS, // AGC: Same as milho with shorter cycle
    trigo: TRIGO_TASKS,
    feijao: FEIJAO_TASKS,
    feijao_carioca: FEIJAO_TASKS,
    feijao_preto: FEIJAO_TASKS,
    algodao: ALGODAO_TASKS,
    // AGC: Add more specific templates as needed
  }
  
  return templates[cropType] || GENERIC_TASKS
}

/**
 * AGC: Generate complete tasks based on crop type
 * AGC: Calculates dates relative to planting date (today + 7 days)
 */
const generateMockTasks = (cropType: string): PlannedTask[] => {
  const today = new Date()
  const template = getCropManagementTemplate(cropType)
  
  // AGC: Generate dates relative to planting - never before today
  const addDays = (days: number) => {
    const date = new Date(today)
    date.setDate(date.getDate() + Math.max(0, days))
    return date.toISOString().split('T')[0]
  }
  
  // AGC: Calculate interval between tasks based on crop cycle
  const numTasks = template.tasks.length
  const cycleDays = template.cycleDays
  
  // AGC: Distribute tasks across the crop cycle
  // AGC: Pre-planting tasks (-14 to 0 days), Planting (0-7), Growth phase, Harvest
  const taskDates: number[] = []
  
  template.tasks.forEach((task, index) => {
    // AGC: Estimate task timing based on category and position
    let dayOffset: number
    
    if (task.title.includes('Análise') || task.title.includes('Dessecação Pré')) {
      dayOffset = -14 + (index * 3) // AGC: Pre-planting
    } else if (task.title.includes('Tratamento') || task.title.includes('Calagem')) {
      dayOffset = 0 // AGC: At planting time
    } else if (task.title.includes('Plantio') || task.title.includes('Semeadura')) {
      dayOffset = 7 // AGC: Planting day
    } else if (task.title.includes('Pré-Emergente')) {
      dayOffset = 8 // AGC: Day after planting
    } else if (task.title.includes('Colheita')) {
      dayOffset = cycleDays + 7 // AGC: End of cycle
    } else if (task.title.includes('Dessecação Pré-Colheita') || task.title.includes('Desfolhante')) {
      dayOffset = cycleDays - 7 // AGC: Week before harvest
    } else {
      // AGC: Distribute remaining tasks evenly through growing season
      const growingSeasonDays = cycleDays - 20 // AGC: Exclude pre-planting and harvest
      const taskPosition = index / numTasks
      dayOffset = 14 + Math.floor(taskPosition * growingSeasonDays)
    }
    
    taskDates.push(dayOffset)
  })
  
  // AGC: Create PlannedTask objects
  return template.tasks.map((task, index) => ({
    id: `task-${cropType}-${index}`,
    title: task.title,
    category: task.category,
    icon: task.icon,
    date: addDays(taskDates[index]),
    status: 'pendente' as const,
    progress: 0,
    inputs: task.inputs,
    // AGC: Carry template outputs so the save loop can build TaskOutputEntry[].
    outputs: task.outputs,
    notes: '',
  }))
}

/**
 * AGC: Get category icon component
 */
const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'plantio':
      return <Leaf className="w-5 h-5 text-emerald-600" />
    case 'pulverizacao':
      return <Droplets className="w-5 h-5 text-blue-600" />
    case 'adubacao':
      return <Beaker className="w-5 h-5 text-amber-600" />
    case 'colheita':
      return <Tractor className="w-5 h-5 text-orange-600" />
    default:
      return <Clock className="w-5 h-5 text-gray-600" />
  }
}

/**
 * AGC: Get status badge styling
 */
const getStatusStyle = (status: string) => {
  switch (status) {
    case 'pendente':
      return 'bg-gray-100 text-gray-700'
    case 'em_andamento':
      return 'bg-blue-100 text-blue-700'
    case 'concluida':
      return 'bg-emerald-100 text-emerald-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

/**
 * AGC: Get status i18n key for use with t() at the call site
 */
const getStatusLabel = (status: string) => {
  switch (status) {
    case 'pendente':
      return 'tasks.statusPending'
    case 'em_andamento':
      return 'tasks.statusInProgress'
    case 'concluida':
      return 'tasks.statusCompleted'
    default:
      return status
  }
}

/**
 * AGC: Filter button component
 */
interface FilterButtonProps {
  label: string
  isActive: boolean
  onClick: () => void
  count?: number
}

const FilterButton: React.FC<FilterButtonProps> = ({ label, isActive, onClick, count }) => (
  <button
    onClick={onClick}
    className={`
      px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap
      ${isActive 
        ? 'bg-[#0B5F4A] text-white' 
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }
    `}
  >
    {label}
    {count !== undefined && (
      <span className={`ml-1 ${isActive ? 'text-emerald-200' : 'text-gray-400'}`}>
        ({count})
      </span>
    )}
  </button>
)

/**
 * AGC: TaskCard - Individual task card with editing capabilities
 */
// AGC: Swipe-to-delete constants (same as TasksList/FinancesPage)
const SWIPE_ACTION_WIDTH = 80
const SWIPE_THRESHOLD = 40

interface TaskCardProps {
  task: PlannedTask
  onUpdate: (task: PlannedTask) => void
  onDelete: (taskId: string) => void
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onUpdate, onDelete }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedTask, setEditedTask] = useState(task)

  // AGC: Swipe-to-delete state
  const [swipeOffset, setSwipeOffset] = useState(0)
  const touchStartX = React.useRef(0)
  const touchStartOffset = React.useRef(0)

  const handleSave = () => {
    onUpdate(editedTask)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditedTask(task)
    setIsEditing(false)
  }

  const handleProgressChange = (value: number) => {
    const newStatus = value === 100 ? 'concluida' : value > 0 ? 'em_andamento' : 'pendente'
    setEditedTask(prev => ({ ...prev, progress: value, status: newStatus }))
  }

  // AGC: Swipe handlers (touch + pointer for cross-platform)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartOffset.current = swipeOffset
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - touchStartX.current
    const next = Math.min(0, Math.max(-SWIPE_ACTION_WIDTH, touchStartOffset.current + dx))
    setSwipeOffset(next)
  }
  const handleTouchEnd = () => {
    setSwipeOffset(prev => (prev < -SWIPE_THRESHOLD ? -SWIPE_ACTION_WIDTH : 0))
  }
  const handlePointerDown = (e: React.PointerEvent) => {
    touchStartX.current = e.clientX
    touchStartOffset.current = swipeOffset
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  const handlePointerMove = (e: React.PointerEvent) => {
    if (e.buttons === 0) return
    const dx = e.clientX - touchStartX.current
    const next = Math.min(0, Math.max(-SWIPE_ACTION_WIDTH, touchStartOffset.current + dx))
    setSwipeOffset(next)
  }
  const handlePointerUp = () => {
    setSwipeOffset(prev => (prev < -SWIPE_THRESHOLD ? -SWIPE_ACTION_WIDTH : 0))
  }

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* AGC: Delete action behind card */}
      <div className="absolute inset-y-0 right-0 flex items-center" style={{ width: SWIPE_ACTION_WIDTH }}>
        <button
          className="w-full h-full flex flex-col items-center justify-center bg-red-500 text-white gap-1"
          onClick={() => onDelete(task.id)}
        >
          <Trash2 className="w-5 h-5" />
          <span className="text-xs font-medium">{t('tasks.deleteTask')}</span>
        </button>
      </div>

      {/* AGC: Swipeable card content */}
      <div
        style={{ transform: `translateX(${swipeOffset}px)`, transition: 'transform 150ms ease-out' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
    <GlassCard className="overflow-hidden">
      {/* AGC: Task header */}
      <div
        className="flex items-center gap-3 cursor-pointer"
        onClick={() => !isEditing && setIsExpanded(!isExpanded)}
      >
        <span className="text-2xl">{task.icon}</span>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 truncate">{task.title}</span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusStyle(task.status)}`}>
              {t(getStatusLabel(task.status))}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <Calendar className="w-3 h-3" />
            <span>{new Date(task.date).toLocaleDateString('pt-BR')}</span>
            {task.progress > 0 && (
              <>
                <span>•</span>
                <span>{task.progress}% concluído</span>
              </>
            )}
          </div>
        </div>
        
        <button className="p-1">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
      </div>

      {/* AGC: Expanded content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          {isEditing ? (
            // AGC: Edit mode
            <div className="space-y-4">
              {/* AGC: Date input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data
                </label>
                <input
                  type="date"
                  value={editedTask.date}
                  onChange={(e) => setEditedTask(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B5F4A]"
                />
              </div>

              {/* AGC: Progress slider */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Progresso: {editedTask.progress}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={editedTask.progress}
                  onChange={(e) => handleProgressChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#0B5F4A]"
                />
                <ProgressBar progress={editedTask.progress} className="mt-2" />
              </div>

              {/* AGC: Inputs (insumos) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Insumos
                </label>
                <div className="space-y-2">
                  {editedTask.inputs.map((input, idx) => (
                    <div key={input.id} className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 flex-1">{input.name}</span>
                      <input
                        type="number"
                        value={input.quantity}
                        onChange={(e) => {
                          const newInputs = [...editedTask.inputs]
                          newInputs[idx] = { ...input, quantity: clampDecimals(parseFloat(e.target.value) || 0) }
                          setEditedTask(prev => ({ ...prev, inputs: newInputs }))
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                        step="0.1"
                      />
                      <span className="text-sm text-gray-500 w-16">{input.unit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* AGC: Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações / Comentários
                </label>
                <textarea
                  value={editedTask.notes}
                  onChange={(e) => setEditedTask(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Adicione observações sobre esta tarefa..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B5F4A] resize-none"
                  rows={3}
                />
              </div>

              {/* AGC: Edit actions */}
              <div className="flex gap-2">
                <Button variant="ghost" onClick={handleCancel} icon={<X className="w-4 h-4" />}>
                  Cancelar
                </Button>
                <Button fullWidth onClick={handleSave} icon={<Save className="w-4 h-4" />}>
                  Salvar
                </Button>
              </div>
            </div>
          ) : (
            // AGC: View mode
            <div className="space-y-3">
              {/* AGC: Progress bar */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Progresso</span>
                  <span className="font-medium">{task.progress}%</span>
                </div>
                <ProgressBar progress={task.progress} />
              </div>

              {/* AGC: Inputs */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <Package className="w-4 h-4" />
                  Insumos
                </p>
                <div className="space-y-1">
                  {task.inputs.map((input) => (
                    <div key={input.id} className="flex justify-between text-sm">
                      <span className="text-gray-600">{input.name}</span>
                      <span className="font-medium">{input.quantity} {input.unit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* AGC: Notes */}
              {task.notes && (
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">{task.notes}</p>
                </div>
              )}

              {/* AGC: Edit button */}
              <Button 
                variant="secondary" 
                fullWidth 
                onClick={() => setIsEditing(true)}
                icon={<Edit2 className="w-4 h-4" />}
              >
                Editar
              </Button>
            </div>
          )}
        </div>
      )}
    </GlassCard>
      </div>
    </div>
  )
}

/**
 * AGC: FieldTaskPlanning - Main component
 * AGC: [UNIFIED_FLOW] This is Step 3 of 3 in the unified lot creation flow
 * AGC: [UNIFIED_FLOW] If plotData.id is missing, lot will be saved here before saving tasks
 */
export const FieldTaskPlanning: React.FC<FieldTaskPlanningProps> = ({
  plotData,
  onNavigate,
  onBack,
  wizardMode,
  wizardStep,
  onWizardComplete
}) => {
  const { t } = useTranslation()

  // AGC: [AR] Human label for the per-country "no automatic plan" message.
  const countryLabel = plotData.country === 'AR' ? 'Argentina' : plotData.country === 'BR' ? 'Brasil' : (plotData.country || t('tasks.thisCountry'))

  // AGC: Get addPlot from context to save the field
  const { addPlot, refreshPlots, getPlotById } = useLotes()
  
  // AGC: Get auth for fetching templates
  const { getAuthToken, user } = useAuth()

  // AGC: Warehouse + container data for client-side storage resolution of grain outputs.
  const { warehouses, containers } = useWarehouse()
  
  // AGC: [FIX] Check if lot is ACTUALLY saved in context, not just if it has an ID
  // AGC: Having an ID doesn't mean it's saved - we now pre-generate IDs for audit purposes
  // AGC: We need to check if the lot exists in the context/Firebase
  const existingPlot = plotData.id ? getPlotById(plotData.id) : null
  const lotAlreadySaved = !!existingPlot
  
  // AGC: Loading state for fetching templates
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true)
  
  // AGC: [SINGLE_SOURCE_OF_TRUTH] State for tasks - starts empty, MUST be populated from Admin Panel
  const [tasks, setTasks] = useState<PlannedTask[]>([])
  
  // AGC: [SINGLE_SOURCE_OF_TRUTH] Error state for template loading
  const [templateError, setTemplateError] = useState<string | null>(null)

  // AGC: [AR] Soft gate — no Admin-Panel templates for this crop is NOT a hard error; the user can
  // AGC: create the lot and add tasks manually. Distinct from a real backend failure (templateError).
  const [noAutoPlan, setNoAutoPlan] = useState(false)

  // AGC: [AR][MANEJO] Sowing date the AR engine anchored the cronograma to (YYYY-MM-DD). Persisted as
  // AGC: the lot's plantingDate so the dashboard/growth-stage dates reflect the real sowing window,
  // AGC: not the lot-creation day. null for Brazil / when no AR plan.
  const [arPlantingDate, setArPlantingDate] = useState<string | null>(null)

  // AGC: [SINGLE_SOURCE_OF_TRUTH] Fetch templates from Admin Panel (MANDATORY - no fallback)
  useEffect(() => {
    const loadBackendTemplates = async () => {
      setIsLoadingTemplates(true)
      setTemplateError(null)
      setNoAutoPlan(false)

      try {
        const token = await getAuthToken()

        // AGC: [AR][MANEJO] Argentine lots generate their manejo from the AR engine (phenology-anchored
        // AGC: cronograma + cited inputs/sources), NOT the Brazil task-template path. Brazil is unchanged.
        if (plotData.country === 'AR') {
          const plan = await fetchArManejoPlan({
            cropType: plotData.cropType,
            coordinates: (plotData.coordinates as [number, number][]) || [],
            areaHa: plotData.area ?? null,
            token: token ?? undefined,
          })
          if (plan && plan.data_status === 'ok' && plan.tasks.length > 0) {
            setTasks(arPlanToPlannedTasks(plan, plotData.cropType, t))
            // AGC: [AR][MANEJO][#8] Remember the engine's sowing date so handleFinish can anchor the
            // AGC: lot's plantingDate to it (dashboard dates from the real siembra, not "today").
            if (plan.planting_date) setArPlantingDate(plan.planting_date)
            console.log(`✅ AGC: [AR][MANEJO] Generated ${plan.tasks.length} tasks for ${plotData.cropType} (zone ${plan.zone?.id ?? '?'}, siembra ${plan.planting_date ?? '?'})`)
          } else {
            // AGC: [AR] No phenology/template for this crop×zone — keep the existing manual soft gate.
            setTasks([])
            setNoAutoPlan(true)
            console.warn(`⚠️ AGC: [AR][MANEJO] No plan (${plan?.data_status ?? 'null'}) for "${plotData.cropType}" — manual tasks.`)
          }
          return
        }

        const backendTemplate = await fetchTaskTemplatesFromBackend(plotData.cropType, token)
        
        if (backendTemplate && backendTemplate.tasks.length > 0) {
          // AGC: Use backend templates (ONLY source of truth)
          const backendTasks = generateTasksFromTemplate(backendTemplate, plotData.cropType)
          setTasks(backendTasks)
          console.log(`✅ AGC: [SINGLE_SOURCE_OF_TRUTH] Loaded ${backendTasks.length} tasks from Admin Panel for ${plotData.cropType}`)
        } else {
          // AGC: [AR] No templates configured — soft path: allow creating the lot with 0 tasks.
          setTasks([])
          setNoAutoPlan(true)
          console.warn(`⚠️ AGC: No task templates for "${plotData.cropType}" — allowing manual task creation.`)
        }
      } catch (error) {
        // AGC: [SINGLE_SOURCE_OF_TRUTH] Backend error - show clear message to user
        const errorMsg = `Failed to load task templates: ${error instanceof Error ? error.message : 'Unknown error'}. Check Admin Panel configuration.`
        setTemplateError(errorMsg)
        console.error('❌ AGC: [SINGLE_SOURCE_OF_TRUTH] Error loading backend templates:', error)
      } finally {
        setIsLoadingTemplates(false)
      }
    }
    
    loadBackendTemplates()
  }, [plotData.cropType, getAuthToken])
  
  // AGC: Filter state
  const [filter, setFilter] = useState<TaskFilterStatus>('todas')

  // AGC: Filter counts
  const filterCounts = useMemo(() => ({
    todas: tasks.length,
    pendentes: tasks.filter(t => t.status === 'pendente').length,
    em_andamento: tasks.filter(t => t.status === 'em_andamento').length,
    concluidas: tasks.filter(t => t.status === 'concluida').length,
  }), [tasks])

  // AGC: Filtered tasks
  const filteredTasks = useMemo(() => {
    if (filter === 'todas') return tasks
    if (filter === 'pendentes') return tasks.filter(t => t.status === 'pendente')
    if (filter === 'em_andamento') return tasks.filter(t => t.status === 'em_andamento')
    if (filter === 'concluidas') return tasks.filter(t => t.status === 'concluida')
    return tasks
  }, [tasks, filter])

  // AGC: Update task handler
  const handleUpdateTask = useCallback((updatedTask: PlannedTask) => {
    setTasks(prev => prev.map(t => t.id === updatedTask.id ? updatedTask : t))
  }, [])

  // AGC: Delete task handler (remove from planning list before saving)
  const handleDeleteTask = useCallback((taskId: string) => {
    setTasks(prev => prev.filter(t => t.id !== taskId))
  }, [])

  // AGC: Loading state for saving
  const [isSaving, setIsSaving] = useState(false)

  // AGC: [UNIFIED_FLOW] Finish planning - save lot (if needed) and tasks
  const handleFinish = async () => {
    setIsSaving(true)
    
    try {
      // AGC: [UNIFIED_FLOW] Determine fieldId - use from plotData (comes from FinalizarLote/currentFarm) or fallback
      const fieldId = plotData.fieldId || 'field-1'
      
      // AGC: [AUDIT_FIX] Use existing lot ID from plotData (generated in FinalizarLote)
      // AGC: This ensures all audit events (recommendations, lot creation, inputs) use the same ID
      let plotId = plotData.id
      
      // AGC: [AUDIT_FIX] Only generate a new ID if plotData.id was not provided
      // AGC: This is a fallback for legacy flows or direct navigation
      if (!plotId) {
        plotId = `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        console.log('⚠️ AGC: [AUDIT_FIX] No plotData.id provided, generated fallback ID:', plotId)
      }
      
      // AGC: [UNIFIED_FLOW] If lot doesn't have an ID, save it first
      if (!lotAlreadySaved) {
        console.log('🌱 AGC: [UNIFIED_FLOW] Saving new lot:', plotId)
        console.log('   Field ID:', fieldId)
        console.log('   Name:', plotData.name)
        console.log('   Area:', plotData.area)
        console.log('   Crop:', plotData.cropType)
        console.log('   Detection method:', plotData.detectionMethod || 'polygon')
        
        // AGC: [UNIFIED_FLOW] Save the plot to context with all data including crop type
        // AGC: Include metrics and detection method from the unified flow
        // AGC: [AUDIT_FIX] Pass the plotId so all audit events use the same ID
        await addPlot({
          id: plotId, // AGC: [AUDIT_FIX] Use the ID generated in FinalizarLote for audit consistency
          fieldId: fieldId, // AGC: Use proper fieldId from FarmContext
          farmId: fieldId, // AGC: Alias for clarity
          name: plotData.name,
          area: plotData.area,
          perimeter: plotData.perimeter || plotData.metrics?.perimeterMeters,
          cropType: plotData.cropType as any,
          // AGC: [FIX] Set plantingDate to today if not provided - ensures progress tracking works
          // AGC: [AR][#8] Prefer an explicit plotData date, then the AR engine's anchored sowing date,
          // AGC: then fall back to today (Brazil/unchanged path).
          plantingDate: plotData.plantingDate || arPlantingDate || new Date().toISOString(),
          expectedHarvestDate: undefined,
          growthStage: 'plantio',
          healthStatus: 'bom',
          managementStatus: 'por-semear',
          managementProgress: 0,
          coordinates: plotData.coordinates,
          soilType: undefined,
          irrigated: false,
          yieldEstimate: undefined,
        })
        
        console.log('✅ AGC: [UNIFIED_FLOW] Lot saved to context')
        // AGC: Telemetry — plot entity persisted via wizard flow
        // AGC: duration_ms not independently tracked at this site; 0 is a valid placeholder
        void track('entity_created', {
          entity_type: 'plot',
          source: 'wizard',
          duration_ms: 0,
        })

        // AGC: Refresh plots to sync with Firebase
        await refreshPlots()
      } else {
        console.log('🔄 AGC: [UNIFIED_FLOW] Using existing lot:', plotId)
      }
      
      // AGC: Get auth token
      const token = await getAuthToken()
      
      // AGC: Save all tasks to Firebase
      console.log(`📋 AGC: [UNIFIED_FLOW] Saving ${tasks.length} tasks...`)
      
      for (const task of tasks) {
        // AGC: Map PlannedTask category to TaskCategory
        const categoryMap: Record<string, TaskCategory> = {
          plantio: 'plantio',
          pulverizacao: 'pulverizacao',
          adubacao: 'adubacao',
          colheita: 'colheita',
        }
        
        // AGC: Map status to Task status
        const statusMap: Record<string, 'pending' | 'in-progress' | 'completed'> = {
          pendente: 'pending',
          em_andamento: 'in-progress',
          concluida: 'completed',
        }
        
        // AGC: FIX - Convert task.inputs to proper TaskInputEntry[] format
        // AGC: This ensures inputs are stored as structured data, not just description text
        const lotAreaHa = plotData.area || 1
        const taskInputEntries: TaskInputEntry[] = task.inputs.map(input => {
          const inputSnapshot: InputSnapshot = {
            name: input.name,
            category: input.category || 'other',
            unit: input.unit,
            capturedAt: new Date().toISOString(),
          }
          return {
            inputId: input.id || `input-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            inputName: input.name,
            quantityPerHa: input.quantity, // AGC: Template stores quantity as per-ha value
            totalQuantity: input.quantity * lotAreaHa, // AGC: Calculate total based on lot area
            unit: input.unit,
            inputSnapshot,
            isCustom: false,
            isDiscontinued: false,
          }
        })

        // AGC: Build outputs payload — Phase 1 grain-output spec.
        // AGC: Resolve storage client-side: single candidate auto-picked, 0/2+ left unset
        // AGC: so backend can return no_storage / needs_storage as appropriate.
        const outputsPayload: TaskOutputEntry[] = (task.outputs || []).map(output => {
          const expectedTotalQuantity = output.expectedYieldPerHa * lotAreaHa
          const { targetContainerId, targetWarehouseId, decidedBy } = resolveStorageForOutputClient({
            containers,
            warehouses,
            grainOutputId: output.outputId,
            grainOutputName: output.outputName,
          })
          return {
            outputId: output.outputId,
            outputName: output.outputName,
            category: output.category,
            expectedYieldPerHa: output.expectedYieldPerHa,
            expectedTotalQuantity,
            unit: output.unit,
            inventoryItemId: '', // AGC: backend will resolve / auto-create and overwrite
            targetContainerId,
            targetWarehouseId,
            storageDecidedBy: decidedBy,
          }
        })

        await createTask({
          title: task.title,
          description: `Insumos: ${task.inputs.map(i => `${i.name}: ${i.quantity} ${i.unit}`).join(', ')}${task.notes ? `\n\nObservações: ${task.notes}` : ''}`,
          fieldId: fieldId, // AGC: Use proper fieldId
          fieldName: plotData.name,
          plotId: plotId || `plot-${Date.now()}`,
          plotName: plotData.name,
          category: categoryMap[task.category] || 'outros',
          status: statusMap[task.status] || 'pending',
          priority: 'medium',
          progress: task.progress,
          dueDate: task.date,
          assignedTo: user?.displayName || 'Não atribuído',
          assignedToId: user?.uid || '', // AGC: Store UID for filtering
          createdBy: user?.uid || '', // AGC: Store creator UID for filtering
          timeline: [],
          inputs: taskInputEntries, // AGC: FIX - Include structured inputs array
          ...(outputsPayload.length > 0 && { outputs: outputsPayload }), // AGC: Include grain outputs when present
        }, token)
      }
      
      console.log('✅ AGC: [UNIFIED_FLOW] All tasks saved', { plotId, tasks: tasks.length })
      
      // AGC: [AUDIT] Emit INPUTS_GENERATED event with inputs summary
      try {
        const backendUrl = getBackendUrl()
        
        // AGC: Collect all unique inputs from all tasks
        const allInputs: Array<{ genericName: string; quantityPerHa: number; totalQuantity: number; unit: string }> = []
        const seenInputs = new Set<string>()
        
        for (const task of tasks) {
          for (const input of task.inputs) {
            const key = `${input.name}-${input.unit}`
            if (!seenInputs.has(key)) {
              seenInputs.add(key)
              allInputs.push({
                genericName: input.name,
                quantityPerHa: input.quantity,
                totalQuantity: input.quantity * (plotData.area || 1),
                unit: input.unit,
              })
            }
          }
        }
        
        await fetch(`${backendUrl}/api/v1/lots/${plotId || 'unknown'}/audit-log/inputs-generated?fieldId=${fieldId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-id': user?.uid || '',
            'x-field-id': fieldId,
          },
          body: JSON.stringify({
            taskCount: tasks.length,
            inputs: allInputs,
          }),
        })
        console.log('📋 AGC: [AUDIT] INPUTS_GENERATED event emitted')
      } catch (auditError) {
        // AGC: Don't fail the main operation if audit fails
        console.warn('⚠️ AGC: [AUDIT] Failed to emit inputs generated event:', auditError)
      }
      
      if (wizardMode && onWizardComplete) {
        onWizardComplete()
      } else {
        // AGC: Navigate to fields list
        onNavigate('lotes-list')
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      console.error('❌ AGC: Error saving tasks:', msg, error)
      alert(`Erro ao salvar tarefas: ${msg}`)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-24">
      {/* AGC: Top Bar */}
      <TopBar
        title={t('fields.taskPlanning')}
        subtitle={wizardMode ? t('fields.wizardLotStep4of5') : t('fields.step3of3')}
        onBack={onBack}
      />

      {/* AGC: [UNIFIED_FLOW] Progress indicator - wizard or standalone */}
      {wizardMode ? (
        <WizardLotProgressBar currentStep={wizardStep || 4} />
      ) : (
        <div className="px-4 py-3">
          <div className="flex gap-2 mb-4">
            <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
            <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
            <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
          </div>
        </div>
      )}

      {/* AGC: Field summary */}
      <div className="px-4 pb-3">
        <GlassCard className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800" padding="sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">🌱</span>
              <div>
                <span className="font-medium text-emerald-800">{plotData.name}</span>
                <p className="text-xs text-emerald-600">
                  {formatAreaHa(plotData.area)} • {plotData.cropType || 'Cultura não definida'}
                </p>
              </div>
            </div>
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          </div>
        </GlassCard>
      </div>

      {/* AGC: Filter tabs */}
      <div className="px-4 py-2 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          <FilterButton
            label={t('common.all')}
            isActive={filter === 'todas'}
            onClick={() => setFilter('todas')}
            count={filterCounts.todas}
          />
          <FilterButton
            label={t('tasks.statusPending')}
            isActive={filter === 'pendentes'}
            onClick={() => setFilter('pendentes')}
            count={filterCounts.pendentes}
          />
          <FilterButton
            label={t('tasks.statusInProgress')}
            isActive={filter === 'em_andamento'}
            onClick={() => setFilter('em_andamento')}
            count={filterCounts.em_andamento}
          />
          <FilterButton
            label={t('tasks.statusCompleted')}
            isActive={filter === 'concluidas'}
            onClick={() => setFilter('concluidas')}
            count={filterCounts.concluidas}
          />
        </div>
      </div>

      {/* AGC: Tasks list */}
      <div className="px-4 py-4 space-y-3">
        {/* AGC: [SINGLE_SOURCE_OF_TRUTH] Show loading state while fetching from Admin Panel */}
        {isLoadingTemplates ? (
          <GlassCard className="text-center py-12">
            <Loader2 className="w-12 h-12 text-[#0B5F4A] mx-auto mb-4 animate-spin" />
            <p className="text-gray-700 font-medium mb-1">{t('tasks.loadingTemplates')}</p>
            <p className="text-sm text-gray-500">{t('tasks.fetchingTemplates', { crop: plotData.cropType })}</p>
          </GlassCard>
        ) : noAutoPlan ? (
          // AGC: [AR] Soft gate — automatic plan not available; user can still create the lot.
          <GlassCard className="text-center py-10 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
            <AlertCircle className="w-14 h-14 text-blue-500 mx-auto mb-4" />
            <p className="text-blue-800 dark:text-blue-200 font-semibold mb-2">{t('tasks.noAutoPlanTitle')}</p>
            <p className="text-blue-700 dark:text-blue-300 text-sm max-w-md mx-auto">
              {t('tasks.noAutoPlanMessage', { country: countryLabel })}
            </p>
          </GlassCard>
        ) : templateError ? (
          // AGC: [SINGLE_SOURCE_OF_TRUTH] Show error if Admin Panel templates are not configured
          <GlassCard className="text-center py-12 bg-red-50 border-red-200">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-red-800 font-semibold mb-2">Templates não configurados</p>
            <p className="text-red-600 text-sm mb-4 max-w-md mx-auto">{templateError}</p>
            <div className="flex flex-col gap-2 max-w-xs mx-auto">
              <Button
                variant="secondary"
                fullWidth
                onClick={onBack}
              >
                {t('tasks.backChooseOtherCrop')}
              </Button>
              <p className="text-xs text-red-500 mt-2">
                Administradores: Configure os templates em Admin Panel → Task Templates → {plotData.cropType}
              </p>
            </div>
          </GlassCard>
        ) : filteredTasks.length > 0 ? (
          filteredTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onUpdate={handleUpdateTask}
              onDelete={handleDeleteTask}
            />
          ))
        ) : (
          <GlassCard className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('tasks.noTasksFilter')}</p>
          </GlassCard>
        )}
      </div>

      {/* AGC: Info card */}
      <div className="px-4 pb-4">
        <GlassCard className="bg-blue-50 border-blue-200">
          <div className="flex gap-3">
            <span className="text-xl">💡</span>
            <div>
              <p className="font-medium text-blue-800">{t('tasks.recsBanner.title')}</p>
              <p className="text-sm text-blue-700 mt-1">
                {t('tasks.recsBanner.desc')}
              </p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* AGC: Footer - safe-area-bottom for Android nav bar */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-4 bg-white border-t border-gray-200"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      >
        <Button fullWidth onClick={handleFinish} disabled={isSaving || isLoadingTemplates || !!templateError || (tasks.length === 0 && !noAutoPlan)}>
          {isSaving ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('tasks.savingTasks', { count: tasks.length })}
            </span>
          ) : isLoadingTemplates ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('tasks.loadingTemplates')}
            </span>
          ) : templateError ? (
            t('tasks.templatesNotConfigured')
          ) : noAutoPlan ? (
            t('tasks.createLotAddTasksLater')
          ) : tasks.length === 0 ? (
            t('tasks.noTasksAvailable')
          ) : (
            t('tasks.finishPlanning', { count: tasks.length })
          )}
        </Button>
      </div>
    </div>
  )
}

export default FieldTaskPlanning






